import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MdUpload, MdCloudUpload, MdCheckCircle, MdDownload, MdMenuBook, MdSave } from 'react-icons/md';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import MainButton from '../../../shared/layout/buttons/MainButton';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import SectionTitle from '../../../shared/layout/titles/SectionTitle';
import { SearchableSelect } from '../../../shared/layout/forms/AppSelect';
import {
    analyzeCsvImport,
    uploadCsvImport,
    fetchOrgManufacturers,
    fetchOrgCategories,
    fetchOrgVenuesForImport,
    saveCsvImportMappings,
    runCsvImport,
    fetchCsvImportProgress,
    suggestCsvImportFieldMapping,
    suggestCsvImportCategoryMappings,
    saveCsvImportFieldMapping,
} from '../../../services/Http/Api';

// Iz backenda (CatalogTemplate); ovdje stoje da se ekran moze prikazati i prije
// prve otpreme, kad odgovora jos nema.
const TEMPLATE_URL = 'https://cdn.digiwarranty.app/dwm-uploads/partneri/dwm-katalog-template.csv';
const GUIDE_URL = 'https://cdn.digiwarranty.app/dwm-uploads/partneri/dwm-katalog-upute.md';
const CSV_FIELD_TARGETS = [
    'external_product_id', 'sku', 'sku_or_gtin', 'ean', 'gtin', 'upc', 'mpn',
    'product_name', 'manufacturer_name', 'manufacturer_external_id', 'category_name', 'category_external_id',
    'condition', 'warranty_months', 'price', 'discounted_price', 'discount_percentage', 'discount_value', 'currency',
    'discount_start_at', 'discount_end_at', 'stock_quantity', 'reserved_quantity', 'is_active', 'product_url',
    'image_url', 'additional_image_urls', 'short_description', 'product_description', 'venue_name', 'venue_external_id',
];
const CSV_REQUIRED_FIELDS = ['product_name', 'price', 'product_url'];
const CSV_IDENTITY_FIELDS = ['external_product_id', 'sku', 'sku_or_gtin', 'ean', 'gtin', 'upc'];

const ImportsPage = () => {
    const { t } = useTranslation();
    const [selectedFile, setSelectedFile] = useState(null);
    const importType = 'catalog';
    const [uploadResult, setUploadResult] = useState(null);
    const [analyzeResult, setAnalyzeResult] = useState(null);
    const [manufacturersOptions, setManufacturersOptions] = useState([]);
    const [categoriesOptions, setCategoriesOptions] = useState([]);
    const [venueOptions, setVenueOptions] = useState([]);
    const [manufacturerMappings, setManufacturerMappings] = useState({});
    const [categoryMappings, setCategoryMappings] = useState({});
    // venueMappings: { [external_id]: { action: 'map'|'create'|null, venue_id?, name?, city?, address?, country_id? } }
    const [venueMappings, setVenueMappings] = useState({});
    const [mappingSaved, setMappingSaved] = useState(false);
    const [fieldMapping, setFieldMapping] = useState({});
    const [fieldMappingSuggestions, setFieldMappingSuggestions] = useState({});
    const [savingFields, setSavingFields] = useState(false);
    const [runSummary, setRunSummary] = useState(null);
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [wizardStep, setWizardStep] = useState(1);
    const pollRef = useRef(null);

    useEffect(() => {
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
            }
        };
    }, []);

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files?.[0] || null);
        setUploadResult(null);
        setAnalyzeResult(null);
        setManufacturerMappings({});
        setCategoryMappings({});
        setVenueMappings({});
        setMappingSaved(false);
        setFieldMapping({});
        setFieldMappingSuggestions({});
        setRunSummary(null);
        setProgress(null);
        setError(null);
        setWizardStep(1);
    };

    const analyzeAndSuggestCategories = async (importId) => {
        const analysis = await analyzeCsvImport(importId);
        setAnalyzeResult(analysis);
        applyAnalyzeMappings(analysis);
        await loadMappingOptions(importType);

        if ((analysis.categories || []).length > 0) {
            try {
                await suggestCsvImportCategoryMappings(importId);
                const refreshed = await analyzeCsvImport(importId);
                setAnalyzeResult(refreshed);
                applyAnalyzeMappings(refreshed);
            } catch (suggestionError) {
                // Keep the regular analysis available if Gemini is unavailable.
                setError(suggestionError);
            }
        }
        setWizardStep(3);
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await uploadCsvImport(selectedFile, importType);
            setUploadResult(data);
            let mapping = { ...(data.field_mapping || {}) };
            setFieldMapping(mapping);
            setFieldMappingSuggestions({});
            setAnalyzeResult(null);
            setRunSummary(null);
            setProgress(null);

            if (!data?.field_mapping_reused && data?.columns_detected?.length) {
                setWizardStep(2);
                try {
                    const ai = await suggestCsvImportFieldMapping(data.import_id);
                    const validSuggestions = Object.fromEntries(
                        Object.entries(ai.suggestions || {}).filter(([target]) => CSV_FIELD_TARGETS.includes(target))
                    );
                    setFieldMappingSuggestions(validSuggestions);
                    Object.entries(validSuggestions).forEach(([target, suggestion]) => {
                        if (suggestion?.source_field && Number(suggestion.confidence) >= 0.9) {
                            mapping[target] = suggestion.source_field;
                        }
                    });
                    setFieldMapping(mapping);
                } catch (aiError) {
                    // Deterministic header matches remain available for review.
                }
            }

            const hasIdentity = CSV_IDENTITY_FIELDS.some((field) => mapping[field]);
            const hasRequired = CSV_REQUIRED_FIELDS.every((field) => mapping[field]);
            if (data?.field_mapping_reused || (hasIdentity && hasRequired)) {
                if (!data?.field_mapping_reused) {
                    const saved = await saveCsvImportFieldMapping(data.import_id, mapping);
                    setUploadResult((current) => ({
                        ...current,
                        status: 'validated',
                        field_mapping_reused: true,
                        field_mapping: saved.field_mapping,
                    }));
                }
                setWizardStep(3);
                await analyzeAndSuggestCategories(data.import_id);
            }
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleAnalyze = async () => {
        if (!uploadResult?.import_id) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await analyzeCsvImport(uploadResult.import_id);
            setAnalyzeResult(data);
            setMappingSaved(false);
            setRunSummary(null);
            setProgress(null);
            applyAnalyzeMappings(data);
            if (importType === 'catalog' || importType === 'stock') {
                await loadMappingOptions(importType);
            }
            if (importType === 'catalog' && (data.categories || []).length > 0) {
                await suggestCsvImportCategoryMappings(uploadResult.import_id);
                const refreshed = await analyzeCsvImport(uploadResult.import_id);
                setAnalyzeResult(refreshed);
                applyAnalyzeMappings(refreshed);
            }
            setWizardStep(3);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveFieldMapping = async () => {
        if (!uploadResult?.import_id) return;
        const hasIdentity = CSV_IDENTITY_FIELDS.some((field) => fieldMapping[field]);
        const missing = [...CSV_REQUIRED_FIELDS.filter((field) => !fieldMapping[field])];
        if (!hasIdentity) missing.push('sku_or_external_product_id_or_gtin');
        if (missing.length) {
            setError({ message: t('Imports.fieldMappingMissing', { fields: missing.join(', '), defaultValue: `Map the required fields: ${missing.join(', ')}` }) });
            return;
        }

        setSavingFields(true);
        setLoading(true);
        setError(null);
        try {
            const saved = await saveCsvImportFieldMapping(uploadResult.import_id, fieldMapping);
            setUploadResult((current) => ({ ...current, status: 'validated', field_mapping_reused: true, field_mapping: saved.field_mapping }));
            const analysis = await analyzeCsvImport(uploadResult.import_id);
            setAnalyzeResult(analysis);
            setMappingSaved(false);
            setRunSummary(null);
            setProgress(null);
            applyAnalyzeMappings(analysis);
            await loadMappingOptions(importType);
            if ((analysis.categories || []).length > 0) {
                await suggestCsvImportCategoryMappings(uploadResult.import_id);
                const refreshed = await analyzeCsvImport(uploadResult.import_id);
                setAnalyzeResult(refreshed);
                applyAnalyzeMappings(refreshed);
            }
            setWizardStep(3);
        } catch (err) {
            setError(err);
        } finally {
            setSavingFields(false);
            setLoading(false);
        }
    };

    const loadMappingOptions = async (type) => {
        try {
            const needsProductMappings = type === 'catalog';
            const needsVenues = type === 'stock' || type === 'catalog';

            const [manufacturers, categories, venues] = await Promise.all([
                needsProductMappings ? fetchOrgManufacturers({ all: true }) : Promise.resolve([]),
                needsProductMappings ? fetchOrgCategories() : Promise.resolve([]),
                needsVenues ? fetchOrgVenuesForImport() : Promise.resolve([]),
            ]);

            setManufacturersOptions(
                (manufacturers || []).map((item) => ({ value: item.id, label: item.name }))
            );
            setCategoriesOptions(
                (categories || []).map((item) => ({ value: item.id, label: item.name }))
            );
            // venues endpoint returns paginated { data: [...] } or plain array
            const venueList = venues?.data ?? (Array.isArray(venues) ? venues : []);
            setVenueOptions(
                venueList.map((v) => ({
                    value: v.id,
                    label: `${v.name}${v.city ? ` (${v.city})` : ''}`,
                }))
            );
        } catch (err) {
            setError(err);
        }
    };

    const applyAnalyzeMappings = (analysis) => {
        const manufacturerState = {};
        (analysis?.manufacturers || []).forEach((item) => {
            const key = item.external_id || item.name;
            if (item.mapped_id) {
                manufacturerState[key] = item.mapped_id;
            } else if (item.mapped_action === 'create') {
                manufacturerState[key] = '__create__';
            } else {
                // Unknown source brands are created during import by default;
                // the retailer can still link them to an existing manufacturer.
                const normalizedName = String(item.name || '').trim().toLowerCase();
                const placeholderNames = ['unknown', 'unknown manufacturer', 'n/a', 'na', 'none', 'no brand', 'unbranded', 'generic', 'modex shop', 'modex-shop'];
                if (normalizedName && !placeholderNames.includes(normalizedName)) {
                    manufacturerState[key] = '__create__';
                }
            }
        });

        const categoryState = {};
        (analysis?.categories || []).forEach((item) => {
            const key = item.external_id || item.name;
            if (item.mapped_id) {
                categoryState[key] = item.mapped_id;
            } else if (item.mapped_action === 'create') {
                categoryState[key] = '__create__';
            } else if (item.suggested_mapping && Number(item.suggested_mapping.confidence) >= 90) {
                // Only high-confidence Gemini suggestions can be preselected
                // for the batch confirmation. Weaker suggestions remain
                // visible for review, rather than being silently confirmed.
                categoryState[key] = `gpt:${item.suggested_mapping.id}`;
            }
        });

        const venueState = {};
        (analysis?.venues || []).forEach((item) => {
            if (item.matched_venue_id) {
                // Auto-matched — pre-select the mapped venue
                venueState[item.external_id] = {
                    action: 'map',
                    venue_id: item.matched_venue_id,
                };
            } else {
                // Unmatched — pre-fill create form with CSV hint data
                venueState[item.external_id] = {
                    action: null,
                    name: item.name || '',
                    city: item.city || '',
                    address: '',
                    country_id: null,
                };
            }
        });

        setManufacturerMappings(manufacturerState);
        setCategoryMappings(categoryState);
        setVenueMappings(venueState);
    };

    const handleSaveMappings = async () => {
        if (!uploadResult?.import_id) {
            return;
        }
        const manufacturersPayload = (analyzeResult?.manufacturers || []).map((item) => {
            const key = item.external_id || item.name;
            const selected = manufacturerMappings[key];
            const action = selected === '__create__' ? 'create' : selected ? 'map' : null;
            return {
                external_id: key,
                external_name: item.name,
                digiwarranty_id: selected && selected !== '__create__' ? selected : null,
                action,
            };
        });

        const categoriesPayload = (analyzeResult?.categories || []).map((item) => {
            const key = item.external_id || item.name;
            const selected = categoryMappings[key];
            const isGpt = typeof selected === 'string' && selected.startsWith('gpt:');
            const action = selected === '__create__' ? 'create' : selected ? 'map' : null;
            return {
                external_id: key,
                external_name: item.name,
                digiwarranty_id: selected && selected !== '__create__'
                    ? (isGpt ? selected.slice(4) : selected)
                    : null,
                digiwarranty_type: isGpt ? 'category_gpt' : 'category_core',
                action,
            };
        });

        const venuesPayload = (analyzeResult?.venues || []).map((item) => {
            const m = venueMappings[item.external_id] || {};
            return {
                external_id: item.external_id,
                action: m.action || null,
                venue_id: m.action === 'map' ? (m.venue_id || null) : null,
                name: m.name || item.name || null,
                city: m.city || item.city || null,
                address: m.address || null,
                country_id: m.country_id || null,
            };
        });

        setLoading(true);
        setError(null);
        try {
            await saveCsvImportMappings(uploadResult.import_id, {
                manufacturers: manufacturersPayload,
                categories: categoriesPayload,
                venues: venuesPayload,
            });
            setMappingSaved(true);
            setWizardStep(4);
            setRunSummary(null);
            setProgress(null);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const startPolling = (importId) => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
        }

        pollRef.current = setInterval(async () => {
            try {
                const data = await fetchCsvImportProgress(importId);
                setProgress(data);
                if (data?.status && !['processing', 'staged', 'enrichment_pending'].includes(data.status)) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                }
            } catch (err) {
                setError(err);
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        }, 2000);
    };

    const handleRun = async () => {
        if (!uploadResult?.import_id) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await runCsvImport(uploadResult.import_id);
            setRunSummary(data);
            startPolling(uploadResult.import_id);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const manufacturerSelectOptions = [
        { value: '__create__', label: t('Imports.createManufacturer') },
        ...manufacturersOptions,
    ];

    const categorySelectOptions = categoriesOptions;

    const venueSelectOptions = [
        { value: '__create__', label: t('Imports.createVenue') },
        ...venueOptions,
    ];

    const requiresMappings = importType === 'catalog';
    const hasVenues = (analyzeResult?.venues || []).length > 0;
    const requiresVenueMappings = (importType === 'stock' || importType === 'catalog') && hasVenues;
    // Unresolved venues are allowed (they fall back to org-level) — only require mappings saved
    const canRunImport =
        (requiresMappings && mappingSaved) ||
        (requiresVenueMappings && mappingSaved) ||
        (importType === 'discounts' && analyzeResult) ||
        (importType === 'stock' && analyzeResult && !hasVenues);
    const wizardSteps = [
        t('Imports.wizard.upload', { defaultValue: 'Učitaj CSV' }),
        t('Imports.wizard.columns', { defaultValue: 'Provjeri kolone' }),
        t('Imports.wizard.mappings', { defaultValue: 'Proizvođači i kategorije' }),
        t('Imports.wizard.review', { defaultValue: 'Pregled i uvoz' }),
    ];
    const suggestedCategoryCount = (analyzeResult?.categories || []).filter((item) => (
        !item.mapped_id && Number(item.suggested_mapping?.confidence) >= 65
    )).length;
    const visibleFieldTargets = Array.from(new Set([
        ...CSV_REQUIRED_FIELDS,
        ...CSV_IDENTITY_FIELDS,
    ]));
    const optionalFieldTargets = CSV_FIELD_TARGETS.filter((field) => !visibleFieldTargets.includes(field));
    const requiredMappedCount = CSV_REQUIRED_FIELDS.filter((target) => fieldMapping[target]).length
        + (CSV_IDENTITY_FIELDS.some((target) => fieldMapping[target]) ? 1 : 0);
    const renderFieldMappingRow = (target) => (
        <FieldMappingRow key={target}>
            <FieldMappingDetails>
                <FieldMappingLabel>
                    {t(`Imports.csvField.${target}`, { defaultValue: target.replace(/_/g, ' ') })}
                    {CSV_REQUIRED_FIELDS.includes(target) && <RequiredMark> *</RequiredMark>}
                </FieldMappingLabel>
                {fieldMappingSuggestions[target] && <FieldSuggestionMeta>
                    {t('Imports.fieldMappingAiSuggestion', { confidence: Math.round((fieldMappingSuggestions[target].confidence || 0) * 100) })}
                </FieldSuggestionMeta>}
                {fieldMapping[target] && uploadResult.preview?.[0]?.[fieldMapping[target]] != null && (
                    <FieldExample>{String(uploadResult.preview[0][fieldMapping[target]]).slice(0, 100)}</FieldExample>
                )}
            </FieldMappingDetails>
            <FieldMappingSelect
                value={fieldMapping[target] || ''}
                onChange={(event) => setFieldMapping((current) => ({ ...current, [target]: event.target.value }))}
                aria-label={`${t('Imports.fieldMappingSource', { defaultValue: 'CSV column' })}: ${target}`}
            >
                <option value="">— nije mapirano —</option>
                {(uploadResult.columns_detected || []).map((header) => (
                    <option key={`${target}-${header}`} value={header}>{header}</option>
                ))}
            </FieldMappingSelect>
        </FieldMappingRow>
    );

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle icon={<MdUpload size={28} />}>{t('Imports.title')}</PageTitle>
            </PageHeader>
            <PageContent>
                <WizardProgress aria-label={t('Imports.wizard.title', { defaultValue: 'Koraci uvoza' })}>
                    {wizardSteps.map((label, index) => {
                        const step = index + 1;
                        const complete = step < wizardStep;
                        return (
                            <WizardStep key={label} $active={step === wizardStep} $complete={complete}>
                                <WizardStepNumber>{complete ? '✓' : step}</WizardStepNumber>
                                <span>{label}</span>
                            </WizardStep>
                        );
                    })}
                </WizardProgress>
                {!uploadResult && <StyledCard>
                    <div style={{ marginBottom: '16px' }}>
                        <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827' }}>{t('Imports.uploadTitle')}</h3>
                        <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.95rem' }}>{t('Imports.uploadSubtitle')}</p>
                    </div>

                    <FormRow>
                        <FileInput
                            type="file"
                            accept=".csv,.txt"
                            onChange={handleFileChange}
                        />
                        <MainButton type="button" onClick={handleUpload} disabled={!selectedFile || loading} icon={<MdCloudUpload />}>
                            {loading ? t('Imports.uploading') : t('Imports.uploadButton')}
                        </MainButton>
                    </FormRow>
                    <HelpText>{t('Imports.requiredNote')}</HelpText>

                    {/* Predlozak i upute stoje ovdje, a ne u mailu: trgovac koji
                        uvozi drugi put ne mora traziti staru poruku. */}
                    <ColumnsGuide>
                        <ColumnsGuideTitle>{t('Imports.template.title')}</ColumnsGuideTitle>
                        <ColumnsGuideText>{t('Imports.template.description')}</ColumnsGuideText>
                        <TemplateRow>
                            <TemplateLink href={TEMPLATE_URL} download>
                                <MdDownload size={17} /> {t('Imports.template.download')}
                            </TemplateLink>
                            <TemplateLink href={GUIDE_URL} target="_blank" rel="noopener noreferrer">
                                <MdMenuBook size={17} /> {t('Imports.template.guide')}
                            </TemplateLink>
                        </TemplateRow>
                        <ColumnsGuideText>
                            <strong>{t('Imports.columnsGuide.required')}</strong>{' '}
                            {t('Imports.template.required')}
                        </ColumnsGuideText>
                    </ColumnsGuide>
                </StyledCard>}

                {loading && <LoadingMessage>{t('Imports.loading')}</LoadingMessage>}
                {error && <ErrorMessage>{t('Imports.error')}: {error.message}</ErrorMessage>}

                {uploadResult && (
                    <StyledCard style={{ marginTop: '1.25rem' }}>
                        <div style={{ marginBottom: '1rem' }}>
                            <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827' }}>{t('Imports.validationTitle')}</h3>
                            <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.95rem' }}>{t('Imports.validationSubtitle')}</p>
                        </div>
                        <InfoGrid>
                            <InfoItem>
                                <InfoLabel>{t('Imports.status')}</InfoLabel>
                                <InfoValue>
                                    {uploadResult.field_mapping_reused
                                        ? t('Imports.wizard.columnsReady', { defaultValue: 'Kolone prepoznate' })
                                        : t('Imports.wizard.fileUploaded', { defaultValue: 'Datoteka učitana' })}
                                </InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t('Imports.totalRows')}</InfoLabel>
                                <InfoValue>{uploadResult.total_rows}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t('Imports.missingColumns')}</InfoLabel>
                                <InfoValue>
                                    {uploadResult.missing_required?.length
                                        ? uploadResult.missing_required.join(', ')
                                        : t('Imports.none')}
                                </InfoValue>
                            </InfoItem>
                        </InfoGrid>

                        {uploadResult.field_mapping && !analyzeResult && (
                            <FieldMappingPanel>
                                <FieldMappingHeading>
                                    <div>
                                        <h4>{t('Imports.fieldMappingTitle', { defaultValue: 'Provjera kolona' })}</h4>
                                    <p>{t('Imports.fieldMappingIntro')}</p>
                                </div>
                                    <FieldCount>{t('Imports.fieldMappingRequiredCount', { mapped: requiredMappedCount, total: CSV_REQUIRED_FIELDS.length + 1 })}</FieldCount>
                                </FieldMappingHeading>
                                <FieldMappingGroupTitle>{t('Imports.fieldMappingRequiredTitle')}</FieldMappingGroupTitle>
                                <FieldMappingHint>{t('Imports.fieldMappingRequiredHelp')}</FieldMappingHint>
                                <FieldMappingRows>
                                    {CSV_REQUIRED_FIELDS.map(renderFieldMappingRow)}
                                </FieldMappingRows>
                                <FieldMappingGroupTitle $spaced>{t('Imports.fieldMappingIdentifierTitle')}</FieldMappingGroupTitle>
                                <FieldMappingHint>{t('Imports.fieldMappingIdentifierHelp')}</FieldMappingHint>
                                <FieldMappingRows>
                                    {CSV_IDENTITY_FIELDS.map(renderFieldMappingRow)}
                                </FieldMappingRows>
                                {optionalFieldTargets.length > 0 && (
                                    <OptionalFields>
                                        <summary>{t('Imports.optionalFieldsTitle')} <span>{t('Imports.optionalFieldsCount', { count: optionalFieldTargets.length })}</span></summary>
                                        <FieldMappingHint>{t('Imports.optionalFieldsHelp')}</FieldMappingHint>
                                        <FieldMappingRows>
                                            {optionalFieldTargets.map(renderFieldMappingRow)}
                                        </FieldMappingRows>
                                    </OptionalFields>
                                )}
                                <ActionRow>
                                    <MainButton type="button" onClick={handleSaveFieldMapping} disabled={loading || savingFields} icon={<MdSave />}>
                                        {savingFields ? t('Imports.fieldMappingSaving', { defaultValue: 'Saving mapping…' }) : t('Imports.fieldMappingSave', { defaultValue: 'Save mapping and continue' })}
                                    </MainButton>
                                </ActionRow>
                                <HelpText>{t('Imports.fieldMappingReusable', { defaultValue: 'After you confirm it, this mapping is reused by this organization for future CSV files with the same headers.' })}</HelpText>
                            </FieldMappingPanel>
                        )}

                        {/* Nepoznati stupci nisu greska - izvoz cesto nosi i
                            stupce za trgovcev vlastiti sustav - ali neka se vidi
                            da ih ne citamo. */}
                        {uploadResult.unknown_columns?.length > 0 && (
                            <NoteBox>
                                {t('Imports.unknownColumns', { columns: uploadResult.unknown_columns.join(', ') })}
                            </NoteBox>
                        )}
                        {!analyzeResult && uploadResult.status === 'validated' && uploadResult.field_mapping_reused && (
                            <ActionRow>
                                <MainButton variant="secondary" type="button" onClick={handleAnalyze} disabled={loading}>
                                    {t('Imports.analyzeButton')}
                                </MainButton>
                            </ActionRow>
                        )}
                    </StyledCard>
                )}

                {/* Razlika prije potvrde. Broj procitanih redaka trgovcu ne
                    znaci nista; zanima ga sto je novo, sto vec ima i gdje se
                    promijenila cijena. */}
                {analyzeResult?.diff && wizardStep === 4 && (
                    <StyledCard style={{ marginTop: '1.25rem' }}>
                        <div style={{ marginBottom: '1rem' }}>
                            <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827' }}>{t('Imports.diff.title')}</h3>
                            <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.95rem' }}>{t('Imports.diff.subtitle')}</p>
                        </div>

                        <DiffGrid>
                            <DiffTile $tone="new">
                                <DiffValue>{analyzeResult.diff.new_products}</DiffValue>
                                <DiffLabel>{t('Imports.diff.newProducts')}</DiffLabel>
                                {/* "Nemam ga ja" i "nema ga nigdje" su dva
                                    razlicita odgovora: vecina "novih" se veze na
                                    artikl koji katalog vec ima, umjesto da
                                    nastane duplikat. */}
                                {analyzeResult.diff.linking_to_known > 0 && (
                                    <DiffSub>
                                        {t('Imports.diff.linkingToKnown', { count: analyzeResult.diff.linking_to_known })}
                                    </DiffSub>
                                )}
                                {analyzeResult.diff.brand_new > 0 && (
                                    <DiffSub>
                                        {t('Imports.diff.brandNew', { count: analyzeResult.diff.brand_new })}
                                    </DiffSub>
                                )}
                            </DiffTile>
                            <DiffTile $tone="existing">
                                <DiffValue>{analyzeResult.diff.existing_products}</DiffValue>
                                <DiffLabel>{t('Imports.diff.existingProducts')}</DiffLabel>
                            </DiffTile>
                            <DiffTile $tone={analyzeResult.diff.price_changes > 0 ? 'changed' : undefined}>
                                <DiffValue>{analyzeResult.diff.price_changes}</DiffValue>
                                <DiffLabel>{t('Imports.diff.priceChanges')}</DiffLabel>
                            </DiffTile>
                            <DiffTile>
                                <DiffValue>{analyzeResult.diff.without_barcode}</DiffValue>
                                <DiffLabel>{t('Imports.diff.withoutBarcode')}</DiffLabel>
                            </DiffTile>
                            <DiffTile $tone={analyzeResult.diff.invalid > 0 ? 'bad' : undefined}>
                                <DiffValue>{analyzeResult.diff.invalid}</DiffValue>
                                <DiffLabel>{t('Imports.diff.invalid')}</DiffLabel>
                            </DiffTile>
                        </DiffGrid>

                        {analyzeResult.new_manufacturers?.length > 0 && (
                            <NoteBox $tone="warn">
                                <strong>{t('Imports.diff.newManufacturers')}</strong>{' '}
                                {analyzeResult.new_manufacturers
                                    .map((m) => `${m.name} (${m.count})`)
                                    .join(', ')}
                            </NoteBox>
                        )}

                        {analyzeResult.unmappable_categories?.length > 0 && (
                            <NoteBox $tone="warn">
                                <strong>{t('Imports.diff.unmappableCategories')}</strong>{' '}
                                {analyzeResult.unmappable_categories
                                    .map((c) => `${c.name} (${c.count})`)
                                    .join(', ')}
                            </NoteBox>
                        )}

                        {analyzeResult.invalid_rows?.length > 0 && (
                            <>
                                <SectionTitle>{t('Imports.diff.invalidRows')}</SectionTitle>
                                <DiffTable>
                                    <thead>
                                        <tr>
                                            <th>{t('Imports.diff.line')}</th>
                                            <th>{t('Imports.diff.product')}</th>
                                            <th>{t('Imports.diff.problem')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {analyzeResult.invalid_rows.map((r) => (
                                            <tr key={`bad-${r.line}`}>
                                                <td>{r.line}</td>
                                                <td>{r.product_name || '—'}</td>
                                                <td>{r.issues.join('; ')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </DiffTable>
                            </>
                        )}

                        {analyzeResult.price_changes?.length > 0 && (
                            <>
                                <SectionTitle>{t('Imports.diff.priceChangeList')}</SectionTitle>
                                <DiffTable>
                                    <thead>
                                        <tr>
                                            <th>{t('Imports.diff.product')}</th>
                                            <th>{t('Imports.diff.from')}</th>
                                            <th>{t('Imports.diff.to')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {analyzeResult.price_changes.map((r) => (
                                            <tr key={`price-${r.line}`}>
                                                <td>{r.product_name}</td>
                                                <td>{r.from}</td>
                                                <td>
                                                    <PriceTo $up={r.direction === 'up'}>
                                                        {r.direction === 'up' ? '▲' : '▼'} {r.to}
                                                    </PriceTo>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </DiffTable>
                            </>
                        )}
                    </StyledCard>
                )}

                {analyzeResult && (wizardStep === 3 || wizardStep === 4) && (
                    <StyledCard style={{ marginTop: '1.25rem' }}>
                        <div style={{ marginBottom: '1rem' }}>
                            <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827' }}>{t('Imports.analysisTitle')}</h3>
                            <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.95rem' }}>{t('Imports.analysisSubtitle')}</p>
                        </div>

                        {wizardStep === 4 && <InfoGrid>
                            <InfoItem>
                                <InfoLabel>{t('Imports.manufacturers')}</InfoLabel>
                                <InfoValue>{analyzeResult.manufacturers?.length || 0}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t('Imports.categories')}</InfoLabel>
                                <InfoValue>{analyzeResult.categories?.length || 0}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t('Imports.products')}</InfoLabel>
                                <InfoValue>{analyzeResult.products || 0}</InfoValue>
                            </InfoItem>
                            {(importType === 'catalog' || importType === 'discounts') && (
                                <InfoItem>
                                    <InfoLabel>{t('Imports.discounts')}</InfoLabel>
                                    <InfoValue>{analyzeResult.discounts || 0}</InfoValue>
                                </InfoItem>
                            )}
                            {(importType === 'catalog' || importType === 'discounts') && (
                                <>
                                    <InfoItem>
                                        <InfoLabel>{t('Imports.discountMatches.matched')}</InfoLabel>
                                        <InfoValue>{analyzeResult.discount_matches?.matched || 0}</InfoValue>
                                    </InfoItem>
                                    <InfoItem>
                                        <InfoLabel>{t('Imports.discountMatches.unmatched')}</InfoLabel>
                                        <InfoValue>{analyzeResult.discount_matches?.unmatched || 0}</InfoValue>
                                    </InfoItem>
                                </>
                            )}
                        </InfoGrid>}
                        {wizardStep === 4 && (importType === 'catalog' || importType === 'discounts') && (analyzeResult.discount_preview || []).length > 0 && (
                            <>
                                <SectionTitle>{t('Imports.discountPreview.title')}</SectionTitle>
                                <DiscountTableWrap>
                                    <DiscountTable>
                                        <thead>
                                            <tr>
                                                <th>{t('Imports.discountPreview.row')}</th>
                                                <th>{t('Imports.discountPreview.externalProductId')}</th>
                                                <th>{t('Imports.discountPreview.sku')}</th>
                                                <th>{t('Imports.discountPreview.percentage')}</th>
                                                <th>{t('Imports.discountPreview.value')}</th>
                                                <th>{t('Imports.discountPreview.discountedPrice')}</th>
                                                <th>{t('Imports.discountPreview.startAt')}</th>
                                                <th>{t('Imports.discountPreview.endAt')}</th>
                                                <th>{t('Imports.discountPreview.match')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(analyzeResult.discount_preview || []).map((row) => (
                                                <tr key={`discount-preview-${row.row_number}`}>
                                                    <td>{row.row_number}</td>
                                                    <td>{row.external_product_id || '-'}</td>
                                                    <td>{row.sku_or_gtin || '-'}</td>
                                                    <td>{row.discount_percentage || '-'}</td>
                                                    <td>{row.discount_value || '-'}</td>
                                                    <td>{row.discounted_price || '-'}</td>
                                                    <td>{row.discount_start_at || '-'}</td>
                                                    <td>{row.discount_end_at || '-'}</td>
                                                    <td>{row.matched ? t('Imports.discountPreview.matchYes') : t('Imports.discountPreview.matchNo')}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </DiscountTable>
                                </DiscountTableWrap>
                            </>
                        )}
                        {(requiresMappings || requiresVenueMappings) && wizardStep === 3 && (
                            <>
                                <SectionTitle>{t('Imports.mappingTitle')}</SectionTitle>
                                <MappingBlock>
                                    <MappingTitle>{t('Imports.manufacturerMapping')}</MappingTitle>
                                    {(analyzeResult.manufacturers || []).filter((item) => !item.mapped_id).map((item) => {
                                        const key = item.external_id || item.name;
                                        return (
                                            <MappingRow key={key}>
                                                <MappingSource>
                                                    <strong>{item.name || t('Imports.unknown')}</strong>
                                                    <MappingMeta>{item.external_id || t('Imports.noExternalId')}</MappingMeta>
                                                    {/* Sto je sustav poklopio sam neka se vidi, da se
                                                        ne cini kao propust cekanja na odabir. */}
                                                    {item.mapped_source === 'exact_name' && (
                                                        <AutoBadge>{t('Imports.autoMatched')}</AutoBadge>
                                                    )}
                                                </MappingSource>
                                                <MappingSelect
                                                    value={manufacturerSelectOptions.find((opt) => opt.value === manufacturerMappings[key]) || null}
                                                    onChange={(_, __, option) => {
                                                        setMappingSaved(false);
                                                        setManufacturerMappings((prev) => ({
                                                            ...prev,
                                                            [key]: option ? option.value : null,
                                                        }));
                                                    }}
                                                    options={manufacturerSelectOptions}
                                                    placeholder={t('Imports.selectManufacturer')}
                                                    isClearable
                                                />
                                            </MappingRow>
                                        );
                                    })}
                                </MappingBlock>
                                <MappingBlock>
                                    <MappingTitle>{t('Imports.categoryMapping')}</MappingTitle>
                                    {suggestedCategoryCount > 0 && (
                                        <HelpText>
                                            {t('Imports.wizard.categorySuggestionsReady', {
                                                count: suggestedCategoryCount,
                                                defaultValue: `Gemini je pripremio ${suggestedCategoryCount} prijedloga. Pouzdana mapiranja (90%+) su već odabrana; ostale prijedloge pregledaj. Sve izbore potvrđuješ jednim klikom.`,
                                            })}
                                        </HelpText>
                                    )}
                                    {(analyzeResult.categories || []).filter((item) => {
                                        const key = item.external_id || item.name;
                                        return !item.auto_resolved && !item.mapped_id;
                                    }).map((item) => {
                                        const key = item.external_id || item.name;
                                        const suggestionOption = item.suggested_mapping ? {
                                            value: `gpt:${item.suggested_mapping.id}`,
                                            label: `${item.suggested_mapping.path || item.suggested_mapping.name} · GPT ${item.suggested_mapping.gpt_code}`,
                                        } : null;
                                        const rowCategoryOptions = suggestionOption
                                            ? [...categorySelectOptions, suggestionOption]
                                            : categorySelectOptions;
                                        return (
                                            <MappingRow key={key}>
                                                <MappingSource>
                                                    <strong>{item.name || t('Imports.unknown')}</strong>
                                                    <MappingMeta>{item.external_id || t('Imports.noExternalId')}</MappingMeta>
                                                    {item.mapped_source === 'resolver' && (
                                                        <AutoBadge>{t('Imports.autoMatched')}</AutoBadge>
                                                    )}
                                                    {/* Ove dvije uvoz svrsta, ali ne na jednu kategoriju
                                                        koja bi stala u izbornik - UPS nema most do core-a,
                                                        a "Periferija" se rjesava po proizvodu. */}
                                                    {item.mapped_source === 'resolver_gpt' && (
                                                        <AutoBadge>{t('Imports.autoGoogle')}</AutoBadge>
                                                    )}
                                                    {item.mapped_source === 'per_product' && (
                                                        <AutoBadge>{t('Imports.autoPerProduct')}</AutoBadge>
                                                    )}
                                                    {item.suggested_mapping && (
                                                        <MappingMeta>
                                                            {t('Imports.aiCategorySuggestion', {
                                                                path: item.suggested_mapping.path || item.suggested_mapping.name,
                                                                confidence: item.suggested_mapping.confidence ?? '—',
                                                                status: item.suggested_mapping.status,
                                                            })}
                                                        </MappingMeta>
                                                    )}
                                                    {item.suggested_mapping && categoryMappings[key] !== suggestionOption.value && (
                                                        <MainButton
                                                            variant="secondary"
                                                            type="button"
                                                            onClick={() => {
                                                                setMappingSaved(false);
                                                                setCategoryMappings((prev) => ({ ...prev, [key]: suggestionOption.value }));
                                                            }}
                                                        >
                                                            {t('Imports.useAiCategorySuggestion')}
                                                        </MainButton>
                                                    )}
                                                </MappingSource>
                                                <MappingSelect
                                                    value={rowCategoryOptions.find((opt) => opt.value === categoryMappings[key]) || null}
                                                    onChange={(_, __, option) => {
                                                        setMappingSaved(false);
                                                        setCategoryMappings((prev) => ({
                                                            ...prev,
                                                            [key]: option ? option.value : null,
                                                        }));
                                                    }}
                                                    options={rowCategoryOptions}
                                                    placeholder={t('Imports.selectCategory')}
                                                    isClearable
                                                />
                                            </MappingRow>
                                        );
                                    })}
                                </MappingBlock>
                                {/* Lokacije iz istog kataloskog CSV-a mapiraju se prije uvoza. */}
                                {hasVenues && (
                                    <MappingBlock>
                                        <MappingTitle>{t('Imports.venueMapping')}</MappingTitle>
                                        {(analyzeResult.venues || []).map((item) => {
                                            const m = venueMappings[item.external_id] || {};
                                            const isCreating = m.action === 'create';
                                            const currentSelectValue = m.action === 'map'
                                                ? venueSelectOptions.find((o) => o.value === m.venue_id) || null
                                                : m.action === 'create'
                                                ? { value: '__create__', label: t('Imports.createVenue') }
                                                : null;

                                            return (
                                                <div key={item.external_id}>
                                                    <MappingRow>
                                                        <MappingSource>
                                                            <strong>{item.name || t('Imports.unknown')}</strong>
                                                            <MappingMeta>
                                                                {item.external_id}
                                                                {item.city ? ` · ${item.city}` : ''}
                                                                {` · ${item.rows_count || 0} rows`}
                                                            </MappingMeta>
                                                            {item.status === 'matched' && (
                                                                <MatchBadge>
                                                                    ✓ {t('Imports.autoMatched')}: {item.matched_venue_name}
                                                                </MatchBadge>
                                                            )}
                                                        </MappingSource>
                                                        <MappingSelect
                                                            value={currentSelectValue}
                                                            onChange={(_, __, option) => {
                                                                const isCreate = option?.value === '__create__';
                                                                setVenueMappings((prev) => ({
                                                                    ...prev,
                                                                    [item.external_id]: isCreate
                                                                        ? { action: 'create', name: item.name || '', city: item.city || '', address: '', country_id: null }
                                                                        : option
                                                                        ? { action: 'map', venue_id: option.value }
                                                                        : { action: null, name: item.name || '', city: item.city || '', address: '', country_id: null },
                                                                }));
                                                            }}
                                                            options={venueSelectOptions}
                                                            placeholder={t('Imports.selectVenue')}
                                                            isClearable
                                                        />
                                                    </MappingRow>
                                                    {isCreating && (
                                                        <VenueCreateForm>
                                                            <VenueFormRow>
                                                                <VenueFormField>
                                                                    <VenueFormLabel>{t('Imports.venueName')} *</VenueFormLabel>
                                                                    <VenueFormInput
                                                                        value={m.name || ''}
                                                                        onChange={(e) => setVenueMappings((prev) => ({
                                                                            ...prev,
                                                                            [item.external_id]: { ...prev[item.external_id], name: e.target.value },
                                                                        }))}
                                                                        placeholder={t('Imports.venueNamePlaceholder')}
                                                                    />
                                                                </VenueFormField>
                                                                <VenueFormField>
                                                                    <VenueFormLabel>{t('Imports.venueCity')}</VenueFormLabel>
                                                                    <VenueFormInput
                                                                        value={m.city || ''}
                                                                        onChange={(e) => setVenueMappings((prev) => ({
                                                                            ...prev,
                                                                            [item.external_id]: { ...prev[item.external_id], city: e.target.value },
                                                                        }))}
                                                                        placeholder={t('Imports.venueCityPlaceholder')}
                                                                    />
                                                                </VenueFormField>
                                                                <VenueFormField>
                                                                    <VenueFormLabel>{t('Imports.venueAddress')}</VenueFormLabel>
                                                                    <VenueFormInput
                                                                        value={m.address || ''}
                                                                        onChange={(e) => setVenueMappings((prev) => ({
                                                                            ...prev,
                                                                            [item.external_id]: { ...prev[item.external_id], address: e.target.value },
                                                                        }))}
                                                                        placeholder={t('Imports.venueAddressPlaceholder')}
                                                                    />
                                                                </VenueFormField>
                                                            </VenueFormRow>
                                                        </VenueCreateForm>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </MappingBlock>
                                )}
                                <ActionRow>
                                    <MainButton variant="secondary" type="button" onClick={handleSaveMappings} disabled={loading} icon={mappingSaved ? <MdCheckCircle /> : null}>
                                        {mappingSaved ? t('Imports.mappingsSaved') : t('Imports.saveMappings')}
                                    </MainButton>
                                </ActionRow>
                            </>
                        )}
                    </StyledCard>
                )}

                {canRunImport && wizardStep === 4 && (
                    <StyledCard style={{ marginTop: '20px', borderLeft: '4px solid #821692' }}>
                        <div style={{ marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827' }}>{t('Imports.runTitle')}</h3>
                            <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.95rem' }}>{t(`Imports.runSubtitleByType.${importType}`)}</p>
                        </div>
                        <ActionRow>
                            <MainButton type="button" onClick={handleRun} disabled={loading}>
                                {loading ? t('Imports.running') : t('Imports.runButton')}
                            </MainButton>
                        </ActionRow>
                        {(progress || runSummary) && (
                            <>
                                <SectionTitle>{t('Imports.progressTitle')}</SectionTitle>
                                <InfoGrid>
                                    <InfoItem>
                                        <InfoLabel>{t('Imports.status')}</InfoLabel>
                                        <InfoValue>{progress?.status || runSummary?.status || t('Imports.pending')}</InfoValue>
                                    </InfoItem>
                                    <InfoItem>
                                        <InfoLabel>{t('Imports.processedRows')}</InfoLabel>
                                        <InfoValue>{progress?.processed_rows ?? runSummary?.processed_rows ?? 0}</InfoValue>
                                    </InfoItem>
                                    <InfoItem>
                                        <InfoLabel>{t('Imports.stagedProducts', { defaultValue: 'In catalog pipeline' })}</InfoLabel>
                                        <InfoValue>{progress?.staged_count ?? runSummary?.staged ?? 0}</InfoValue>
                                    </InfoItem>
                                    <InfoItem>
                                        <InfoLabel>{t('Imports.created')}</InfoLabel>
                                        <InfoValue>{progress?.created_count ?? runSummary?.created ?? 0}</InfoValue>
                                    </InfoItem>
                                    <InfoItem>
                                        <InfoLabel>{t('Imports.updated')}</InfoLabel>
                                        <InfoValue>{progress?.updated_count ?? runSummary?.updated ?? 0}</InfoValue>
                                    </InfoItem>
                                    <InfoItem>
                                        <InfoLabel>{t('Imports.skipped')}</InfoLabel>
                                        <InfoValue>{progress?.skipped_count ?? runSummary?.skipped ?? 0}</InfoValue>
                                    </InfoItem>
                                    <InfoItem>
                                        <InfoLabel>{t('Imports.failed')}</InfoLabel>
                                        <InfoValue>{progress?.failed_count ?? runSummary?.failed ?? 0}</InfoValue>
                                    </InfoItem>
                                </InfoGrid>
                            </>
                        )}
                    </StyledCard>
                )}
            </PageContent>
        </PageContainer>
    );
};

export default ImportsPage;

const TemplateRow = styled.div`
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin: 0.75rem 0;
`;

const TemplateLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 0.85rem;
    border: 1px solid rgba(130, 22, 146, 1);
    border-radius: 6px;
    color: rgba(130, 22, 146, 1);
    font-size: 0.88rem;
    font-weight: 600;
    text-decoration: none;

    &:hover {
        background: rgba(130, 22, 146, 1);
        color: #fff;
    }
`;

const NoteBox = styled.div`
    margin-top: 0.85rem;
    padding: 0.65rem 0.8rem;
    border-radius: 6px;
    font-size: 0.86rem;
    line-height: 1.5;
    background: ${({ $tone }) => ($tone === 'warn' ? '#fff8e1' : '#f3f4f6')};
    color: ${({ $tone }) => ($tone === 'warn' ? '#8a6d00' : '#4b5563')};
`;

const DiffGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 0.75rem;
`;

/* Boja nosi znacenje samo uz brojku i natpis, nikad sama: sivo je neutralno,
   zuto trazi pogled, crveno znaci da redak nece uci. */
const DiffTile = styled.div`
    padding: 0.85rem;
    border-radius: 8px;
    border: 1px solid ${({ $tone }) => ({
        new: '#c7e0c9',
        existing: '#cfd8e3',
        changed: '#f0dca8',
        bad: '#eec2c2',
    }[$tone] || '#e5e7eb')};
    background: ${({ $tone }) => ({
        new: '#f2f9f3',
        existing: '#f5f7fa',
        changed: '#fffaf0',
        bad: '#fdf4f4',
    }[$tone] || '#fff')};
`;

const DiffValue = styled.div`
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
`;

const DiffSub = styled.div`
    font-size: 0.74rem;
    color: #6b7280;
    margin-top: 0.3rem;
`;

const DiffLabel = styled.div`
    font-size: 0.8rem;
    color: #6b7280;
    margin-top: 0.15rem;
`;

const DiffTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 1rem;

    th, td {
        text-align: left;
        padding: 0.5rem;
        border-bottom: 1px solid #eceff3;
        font-size: 0.86rem;
    }

    th {
        color: #6b7280;
        font-weight: 600;
    }
`;

const PriceTo = styled.span`
    font-weight: 600;
    color: ${({ $up }) => ($up ? '#b3261e' : '#1b7f3b')};
`;

const WizardProgress = styled.nav`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.5rem;
    margin: 0 0 1rem;
    padding: 0.75rem;
    border: 1px solid #ece8ef;
    border-radius: 12px;
    background: #fff;

    @media (max-width: 720px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const WizardStep = styled.div`
    display: flex;
    align-items: center;
    gap: 0.55rem;
    min-width: 0;
    padding: 0.5rem;
    border-radius: 8px;
    color: ${({ $active, $complete }) => ($active || $complete ? '#61136f' : '#77717b')};
    background: ${({ $active }) => ($active ? '#f6edf8' : 'transparent')};
    font-size: 0.86rem;
    font-weight: ${({ $active }) => ($active ? 700 : 500)};
`;

const WizardStepNumber = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 1.65rem;
    width: 1.65rem;
    height: 1.65rem;
    border-radius: 50%;
    color: white;
    background: #8b1799;
    font-size: 0.78rem;
    font-weight: 700;
`;

const FormRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
`;

const FileInput = styled.input`
    flex: 1;
    min-width: 220px;
    padding: 0.5rem;
    background: #f9fafb;
    border: 1px dashed #d1d5db;
    border-radius: 8px;
    
    &::file-selector-button {
        margin-right: 1rem;
        padding: 0.375rem 0.75rem;
        border-radius: 6px;
        border: none;
        background: #e5e7eb;
        color: #374151;
        font-weight: 600;
        cursor: pointer; 
        transition: background 0.2s;
    }

    &::file-selector-button:hover {
        background: #d1d5db;
    }
`;

const HelpText = styled.p`
    margin-top: 0.75rem;
    color: #6b7280;
    font-size: 0.875rem;
`;

const FieldMappingPanel = styled.section`
    margin-top: 1.25rem;
    padding: clamp(1rem, 2.5vw, 1.75rem);
    border: 1px solid #e9e1ed;
    border-radius: 16px;
    background: linear-gradient(180deg, #fff 0%, #fdfbfe 100%);
    box-shadow: 0 8px 24px rgba(47, 23, 56, 0.045);

    h4 { margin: 0 0 0.35rem; color: #25202a; }
    p { margin: 0; color: #62606a; font-size: 0.9rem; line-height: 1.45; }
`;

const FieldMappingHeading = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.5rem;

    @media (max-width: 640px) {
        flex-direction: column;
    }
`;

const FieldCount = styled.span`
    flex: 0 0 auto;
    padding: 0.4rem 0.7rem;
    border-radius: 999px;
    background: #f4eaf6;
    color: #68147a;
    font-size: 0.78rem;
    font-weight: 700;
`;

const FieldMappingGroupTitle = styled.h5`
    margin: ${({ $spaced }) => ($spaced ? '1.35rem 0 0.35rem' : '0 0 0.35rem')};
    color: #28222e;
    font-size: 0.98rem;
    font-weight: 700;
`;

const FieldMappingHint = styled.p`
    margin: 0 0 0.9rem !important;
    color: #716b78 !important;
    font-size: 0.82rem !important;
`;

const FieldMappingRows = styled.div`
    display: grid;
    gap: 0.65rem;
`;

const FieldMappingRow = styled.div`
    display: grid;
    grid-template-columns: minmax(180px, 0.8fr) minmax(220px, 1.2fr);
    align-items: center;
    gap: 1rem;
    padding: 0.85rem 1rem;
    border: 1px solid #eee8f1;
    border-radius: 11px;
    background: #fff;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
        gap: 0.55rem;
    }
`;

const FieldMappingDetails = styled.div`
    min-width: 0;
`;

const FieldMappingLabel = styled.div`
    color: #302936;
    font-size: 0.88rem;
    font-weight: 650;
    text-transform: capitalize;
`;

const FieldExample = styled.div`
    margin-top: 0.25rem;
    overflow: hidden;
    color: #77717e;
    font-size: 0.77rem;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const FieldMappingSelect = styled.select`
    width: 100%;
    min-width: 0;
    min-height: 42px;
    padding: 0.55rem 0.75rem;
    border: 1px solid #d9d0df;
    border-radius: 9px;
    background: #fff;
    color: #28232c;
    font-size: 0.88rem;
    &:focus { outline: 3px solid rgba(139, 23, 153, 0.15); border-color: #8b1799; }
`;

const OptionalFields = styled.details`
    margin-top: 1.1rem;
    padding-top: 1rem;
    border-top: 1px solid #ece6ef;

    summary {
        display: flex;
        justify-content: space-between;
        cursor: pointer;
        color: #5b176b;
        font-size: 0.88rem;
        font-weight: 700;
    }
    summary span { color: #817987; font-size: 0.78rem; font-weight: 500; }
    &[open] summary { margin-bottom: 0.8rem; }
    ${FieldMappingHint} { margin-top: 0.35rem !important; }
`;

const RequiredMark = styled.span`
    color: #b3261e;
`;

const FieldSuggestionMeta = styled.small`
    display: block;
    margin-top: 0.25rem;
    color: #6b5576;
    font-size: 0.72rem;
    font-weight: 400;
    line-height: 1.35;
    white-space: normal;
`;

const ColumnsGuide = styled.div`
    margin-top: 0.75rem;
    padding: 0.75rem;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
`;

const ColumnsGuideTitle = styled.div`
    font-size: 0.875rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 0.375rem;
`;

const ColumnsGuideText = styled.p`
    margin: 0.25rem 0;
    font-size: 0.875rem;
    color: #374151;
    line-height: 1.4;
`;

const ActionRow = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 1.5rem;
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
`;

const InfoItem = styled.div`
    background: #f9fafb;
    border-radius: 12px;
    padding: 1rem;
    border: 1px solid #f3f4f6;
`;

const InfoLabel = styled.div`
    font-size: 0.75rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
    margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
    font-size: 1.125rem;
    color: #111827;
    font-weight: 600;
`;

const MappingBlock = styled.div`
    border-top: 1px solid #e5e7eb;
    padding-top: 1.25rem;
    margin-top: 1.25rem;
`;

const AutoBadge = styled.span`
    display: inline-block;
    margin-top: 0.25rem;
    padding: 0.12rem 0.45rem;
    border-radius: 4px;
    background: #e8f4ea;
    color: #1b7f3b;
    font-size: 0.72rem;
    font-weight: 600;
`;

const MappingTitle = styled.h4`
    margin: 0 0 1rem;
    color: #374151;
    font-size: 1rem;
`;

const MappingRow = styled.div`
    display: grid;
    grid-template-columns: 1fr minmax(240px, 320px);
    gap: 1rem;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f3f4f6;
    
    &:last-child {
        border-bottom: none;
    }
`;

const MappingSource = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const MappingMeta = styled.span`
    color: #9ca3af;
    font-size: 0.875rem;
`;

const MappingSelect = styled(SearchableSelect)``;

const DiscountTableWrap = styled.div`
    margin-top: 0.75rem;
    overflow-x: auto;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
`;

const DiscountTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;

    th,
    td {
        padding: 0.625rem 0.75rem;
        border-bottom: 1px solid #f3f4f6;
        text-align: left;
        white-space: nowrap;
    }

    th {
        background: #f9fafb;
        color: #374151;
        font-weight: 600;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }
`;

const MatchBadge = styled.span`
    display: inline-block;
    margin-top: 0.125rem;
    padding: 0.125rem 0.5rem;
    background: #dcfce7;
    color: #166534;
    border-radius: 99px;
    font-size: 0.75rem;
    font-weight: 600;
`;

const VenueCreateForm = styled.div`
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 1rem;
    margin: 0.25rem 0 0.75rem;
`;

const VenueFormRow = styled.div`
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
`;

const VenueFormField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    flex: 1;
    min-width: 160px;
`;

const VenueFormLabel = styled.label`
    font-size: 0.8125rem;
    color: #374151;
    font-weight: 600;
`;

const VenueFormInput = styled.input`
    padding: 0.5rem 0.625rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    background: #fff;

    &:focus {
        border-color: #821692;
        outline: none;
        box-shadow: 0 0 0 2px rgba(130, 22, 146, 0.1);
    }
`;
